import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CreditCard, X } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import "./SubscriptionRequiredModal.css";

interface SubscriptionRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout?: () => Promise<void> | void;
}

const SubscriptionRequiredModal: React.FC<SubscriptionRequiredModalProps> = ({
  isOpen,
  onClose,
  onLogout,
}) => {
  const { t } = useTranslation(["common"]);
  const { subscribe, isCreatingCheckout, error } = useSubscription();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!isOpen) return null;

  const handleSubscribe = () => {
    subscribe({
      successUrl: `${window.location.origin}/dashboard?from_stripe=true`,
      cancelUrl: `${window.location.origin}${window.location.pathname}?subscription=cancelled`,
    });
  };

  const handleLogout = async () => {
    if (!onLogout) return;

    try {
      setIsLoggingOut(true);
      await onLogout();
    } catch (err) {
      console.error("Error logging out:", err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="subscription-modal-overlay">
      <div className="subscription-modal">
        <div className="subscription-modal-header">
          <h2>{t("common:subscription.required.title")}</h2>
          <p>{t("common:subscription.required.description")}</p>
        </div>

        <div className="subscription-plan-card">
          <div className="plan-header">
            <h3>{t("common:subscription.required.premiumPlan")}</h3>
            <div className="plan-badge">
              {t("common:subscription.required.recommended")}
            </div>
          </div>

          <div className="plan-features">
            <div className="feature-item">
              <Check size={16} className="feature-icon" />
              <span>{t("common:subscription.features.unlimitedSearches")}</span>
            </div>
            <div className="feature-item">
              <Check size={16} className="feature-icon" />
              <span>{t("common:subscription.features.aiMatching")}</span>
            </div>
            <div className="feature-item">
              <Check size={16} className="feature-icon" />
              <span>{t("common:subscription.features.emailAutomation")}</span>
            </div>
            <div className="feature-item">
              <Check size={16} className="feature-icon" />
              <span>{t("common:subscription.features.prioritySupport")}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="subscription-error">
            <strong>{t("common:status.error")}:</strong> {error}
          </div>
        )}

        <div className="subscription-modal-actions">
          {onLogout && (
            <button
              className="btn-secondary"
              onClick={handleLogout}
              disabled={isCreatingCheckout || isLoggingOut}
            >
              {isLoggingOut ? (
                <>
                  <div className="spinner"></div>
                  {t("common:subscription.required.signingOut")}
                </>
              ) : (
                <>
                  <X size={16} /> {t("common:subscription.required.signOut")}
                </>
              )}
            </button>
          )}

          <button
            className="btn-primary"
            onClick={handleSubscribe}
            disabled={isCreatingCheckout || isLoggingOut}
          >
            {isCreatingCheckout ? (
              <>
                <div className="spinner"></div>
                {t("common:subscription.redirecting")}
              </>
            ) : (
              <>
                <CreditCard size={16} /> {t("common:subscription.subscribeNow")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequiredModal;
